import { render } from 'react-dom'
import './styles.css'
import * as THREE from 'three'
import { useRef, Suspense, useEffect, useState } from 'react'
import { Canvas, useFrame, useThree, extend } from '@react-three/fiber'
import { Center, Bounds, Instances, Instance } from '@react-three/drei'
import * as Tone from 'tone'
import Colors from './Colors'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { AfterimagePass } from 'three/examples/jsm/postprocessing/AfterimagePass'
import { LayerMaterial, Color, Depth, Noise } from 'lamina/vanilla'

function shuffle(array) {
  let currentIndex = array.length, randomIndex;
  while (currentIndex != 0) {
    randomIndex = Math.floor(fxrand() * currentIndex);
    currentIndex--;
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex], array[currentIndex]];
  }
  return array;
}

let randI = (i) => Math.floor(i * fxrand())
let rand = (arr) => arr[randI(arr.length)]
const ci = randI(Colors.length)
const bgColor = Colors[ci][0]
const cols = Colors[ci]
shuffle(cols)

let columns = 16;
let rows = 4;
let boxScale = 1 + randI(10)
let wild = randI(100) > 95
const feats = window.$fxhashFeatures = {
  phases: 2 + randI(3),
  colorMod: Math.floor((10 + randI(35)) * (wild ? 0.2 : 1)),
  border: rand([0.5, 1, 1, 1, 2, 2, 3]),
  bpm: rand([60, 70, 80, 90]) * 0.17,
  phaseNotes: 4 + randI(12),
  steps: 4 + randI(wild ? 14 : 4) * 3,
  sidez: 1 + randI(6),
  xCam: randI(3) * 0.2 * rand([1, -1]) * 0,
  yCam: randI(3) * 0.2 * rand([1, -1]) * 0,
  rads: rand([0.1, 0.25, 0.5, 1, 1.5, 2, 3]),
  totalSteps: (((1000 + randI(600))) - (boxScale * 80)) * (wild ? 0.7 : 1),
  boxScale,
  danceMoves: 4 + randI(20),
  wild,
}

class AfterBurn extends AfterimagePass {
  constructor() {
    super(1)
    this.shaderMaterial.onBeforeCompile = (shader) => {
      shader.fragmentShader =
        `
        uniform float damp;
        uniform sampler2D tOld;
        uniform sampler2D tNew;
        varying vec2 vUv;
    
        vec4 when_gt( vec4 x, float y ) {
          return max( sign( x - y ), 0.0 );
        }

        void main() {
          vec4 texelOld = texture2D( tOld, vUv );
          vec4 texelNew = texture2D( tNew, vUv );
          texelOld *= damp * when_gt( texelOld, 0.00001 );
          gl_FragColor = max(texelNew, texelOld);
        }
        `
    }
  }
}

extend({ EffectComposer, RenderPass, AfterBurn })

const Tint = () => {
  const { size, gl, scene, camera } = useThree()
  const composer = useRef()

  useFrame(() => composer.current.render(), 1)
  return (
    <effectComposer ref={composer} args={[gl]}>
      <renderPass attachFns={['addPass']} args={[scene, camera]} />
      <afterBurn attachFns={['addPass']} args={[size]} />
    </effectComposer>
  )
}
export default function App() {
  const size = useWindowSize();
  useEffect(() => {
    posCount = 0
  }, [size.width])
  const start = () => {
    if (Tone.Transport.state !== 'started') {
      Tone.start();
      Tone.Transport.start();
      parts.forEach((p, i) => { p.start() })
      started = true
      lfo.start()
    }

  }
  isFxpreview && start()
  return (
    <div id="main" onClick={() => start()} key={size.width}>
      <Canvas flat={true} dpr={[1, 2]} camera={{ near: 0.001, far: 10000, position: [feats.xCam, feats.yCam, -9], fov: 160 }}>
        <Stuff />
        <Back />
        <Tint />
        <ambientLight intensity={0.5} />
      </Canvas>
    </div>
  )
}
let s = 0.00001 * feats.boxScale
let geo = new THREE.BoxBufferGeometry(s, s, s)
let started = true;
function Stuff() {
  useFrame(() => {
    if (started) {
      posCount++;
    }
    if (!(posCount % (feats.colorMod * 2))) {
      material.layers[0].color.set(rand(cols))
      material.layers[1].colorA.set(rand(cols))
      material.layers[1].colorB.set(rand(cols))
    }
  })
  let sides = 16
  let r = (Math.PI * 0.0625) / sides
  return (
    <Bounds fit margin={window.innerWidth < 700 ? 0.25 : 0.2} damping={0}>
      <Center>
        <Instances limit={10000} geometry={geo} material={material}>
          {
            new Array(feats.sidez).fill().map((_, zi) =>
              <group key={zi} rotation={[0, 0, ((Math.PI * feats.rads) / feats.sidez) * zi]}>
                {new Array(sides).fill().map((_, si) =>
                  <group rotation={[0, 0, r * si]} key={si}>
                    {new Array(columns).fill().map((_, col) => new Array(rows).fill().map((_, row) => <Thang col={col} row={row} key={row + col} />))}
                  </group>
                )}
              </group>
            )
          }
        </Instances>
      </Center>
    </Bounds>
  )
}

let modes = [
  (t, c, r) => Math.tan(Math.sin(r / 3 - t * 2) + Math.cos(c / 2 - t * 3)),
  (t, c, r) => .02 / Math.sin(3 ** ++c - Math.atan2(c, c * r) * 3 - t),
  (t, c, r) => r / 4 & (c - 8) / r + t * 4,
  (t, c, r) => Math.cos(2 * r / c ^ 2 + t),
  (t, c, r) => ((c * r) % (8 + Math.floor(t % 8))) > 6 ? 1 : -.5,
  (t, c, r) => (c + r < Math.max(rows, columns)) * ((c + r) < Math.ceil(Math.max(rows, columns) * 0.5) ? 1 : -1) || (c - r < 1),
  (t, c, r) => [7, 5, 3, 1].reduce((a, b) => r > a + a / 2 * Math.cos(c / a + (gt * 2) * a) ? -a / 3 : b),
  (t, c, r) => [8, 5, 3, 1].reduce((a, b) => r > a + a / 2 * Math.cos(c / a + gt * a) ? -a / 5 : b),
  (t, c, r) => ((c + (r + t * 9) ^ c - (r + t * 9)) % 13 & 1) - .15,
  (t, c, r) => Math.sin(c / 8 + t) % Math.sin(r + t),
  (t, c, r) => [t % 1, -t % 2, t % 3, -t % 4, t % 5][c * r % 5] / 5,
  (t, c, r) => Math.sin(2 * Math.atan2(r - 8, c) + t),
  (t, c, r) => c < 13 ? ((Date.now() + '')[c] - 5) / 5 : 0,
  (t, c, r) => Math.sin(t) * 5 * Math.sin(r / 5) * Math.sin(c / 5) % 1,
  (t, c, r) => ((4 * 9 + c - r * Math.tan(4) ^ c * Math.tan(4) + r) & 8) - 4,
  // (t,c,r) => Math.sin(Math.atan2(currR.current-=8,currC.current-8)+r*r/40+t),
  (t, c, r) => Math.sin(t * r * c / ((r * columns) + c) * 10),
  (t, c, r) => Math.cos(c / 3 + Math.cos(r / 5 + t * 0.1) * 4) * 2,
  (t, c, r) => Math.cos(t * 1 + Math.sqrt(c * r * 0.05)) + .5,
  (t, c, r) => Math.max(0, Math.sin(r ^ (c + 9 * t))),
  (t, c, r) => Math.min(0, c / r * (Math.hypot(c + Math.cos(t) - (columns * 0.5), r - (rows * 0.5)) - 5))
]

const colorA = new THREE.Color(rand(cols)).convertSRGBToLinear()
const colorB = new THREE.Color(rand(cols)).convertSRGBToLinear()
const material = new LayerMaterial({
  transparent: true,
  opacity: 0.00001,
  layers: [
    new Color({ color: colorA }),
    new Depth({ colorA: colorA, colorB: colorB, alpha: 0.1, mode: 'normal', near: 7, far: 9, origin: [1, 1, 1] }),
    new Noise({ mapping: 'local', type: 'simplex', scale: 100000000, colorA: 'black', colorB: 'black', mode: 'subtract', opacity: 0.25 })
  ]
})
let positions = new Array(feats.danceMoves).fill().map(() =>
  [
    randI(3) * 0.1 * rand([1, -1]),
    randI(3) * 0.1 * rand([1, -1]),
  ]
)
function easeInCirc(x) {
  return x === 1 ? 1 : 1 - Math.pow(2, -10 * x);
}
let positionSteps = positions.map((p, pi) => {
  let p1 = new THREE.Vector2(...p)
  let p2 = new THREE.Vector2(...positions[(pi + 1) % positions.length])

  return new Array(feats.steps).fill().map((s, si) => p1.clone().lerp(p2, easeInCirc(si / feats.steps)))
}
).flat()

let posCount = 0
let mode = 0
function Thang({ col, row }) {
  const ref = useRef();
  let getX = () => (col * 0.01) + positionSteps[posCount % positionSteps.length].x + (Math.floor(posCount / positionSteps.length) * 0.04)
  let getY = () => (row * 0.01) + positionSteps[posCount % positionSteps.length].y + (Math.floor(posCount / positionSteps.length) * 0.04)
  let getZ = () => 0
  let sc = () => modes[mode](feats.wild ? randI(10) : 200, col, row) * (feats.wild ? 0.5 : 1)
  useFrame(() => {
    if (posCount < feats.totalSteps) {
      ref.current.position.x = getX()
      ref.current.position.y = getY()
    } else if ((posCount - 50) > feats.totalSteps && !previewed) {
      fxpreview()
      previewed = true
    }
  })

  return (
    <Instance ref={ref} position={[getX(), getY(), getZ()]} scale={[sc(), sc(), sc()]} />
  )
}
function Back({ col, row }) {
  const ref = useRef();
  useFrame((state) => {
    if (state.clock.getElapsedTime() > 1) {
      ref.current.scale.setScalar(0)
    }
  })
  useEffect(() => {
    setTimeout(() => {
      document.getElementById('borders').style.display = 'unset'
    }, 500)
  }, [])
  let s = 12
  return (
    <group ref={ref}>
      <mesh>
        <meshBasicMaterial color={bgColor} side={THREE.BackSide} />
        <octahedronBufferGeometry args={[s, 3]} />
      </mesh>
    </group>
  )
}

Tone.Transport.bpm.value = feats.bpm
var pianoNotesPool = ["C", "Ds", "Fs"];
let pianoSamples = {};
pianoNotesPool.map((val) => {
  for (var i = 3; i < 7; i++)
    pianoSamples[val.replace(/s/gi, '#') + i] = val + i + 'v' + rand([1,1, 1, 1, 1, 1]) + '.[mp3|ogg]';
})
var pianoParams = [
  pianoSamples,
  {
    "release": 8,
    "attack": 0.3,
    "baseUrl": "./salamanderlight/"
  }]
let pingpong = new Tone.PingPongDelay('4m', 0.25).toDestination()
let lfo = new Tone.LFO(0.05)
lfo.connect(pingpong.feedback)

let sampler = new Tone.Sampler(...pianoParams)

sampler.toDestination()
let durs = ['1m', '2n', '2m', '4m', '4t', '2t', '1t','1m', '2n', '2m', '4m', '4t', '2t', '1t','16t']

let parts = []
let previewed = false

new Array(feats.phases).fill().map((_, j) => {
  let time = 0;
  let dur = 0;
  let phase = new Array(feats.phaseNotes).fill().map((_, i) => {
    let dur = Tone.Time(rand(durs)).toSeconds()
    let note = rand(['C', 'D', 'E', 'F']) + rand([2, 3, 3, 4, 5, 6, 7, 4, 5, 6, 7])
    let vel = 0.12+ (randI(4) * 0.04)

    let res = {
      time,
      dur,
      i,
      j,
      note,
      vel,
    }
    time = + dur
    return res
  })

  let part = new Tone.Part(((time, { note, dur, vel }) => {
    sampler.triggerAttackRelease(note.replace(/s/gi, '#'), dur, time, vel);
    if (randI(100) > 97) {
      let randNote = rand(['C', 'D', 'E', 'F']) + rand([2, 3, 3, 4, 5, 6, 7, 4, 5, 6, 7])
      sampler.triggerAttackRelease(randNote, dur, time, vel);
    }
    if (randI(100) > 98) {
      let randNote = rand(['C', 'D', 'E', 'F']) + rand([2, 3, 3, 4, 5, 6, 7, 4, 5, 6, 7])
      sampler.triggerAttackRelease(randNote, dur, time, vel);
    }
    if (randI(100) > 98) {
      let randNote = rand(['C', 'D', 'E', 'F']) + rand([2, 3, 3, 4, 5, 6, 7, 4, 5, 6, 7])
      sampler.triggerAttackRelease(randNote, dur, time, vel);
    }
  }), phase)

  part.loop = true
  part.loopEnd = time + dur + (randI(4) * 0.0004 * rand([1, -1]))
  parts.push(part)
})
console.log('%c🎹◯ @ramioso 2022-04-25 ◯🎹', 'color: orange');
let b = feats.border + 'vw'
let stylez = { background: bgColor, width: b, height: b }
document.getElementById('root').style.background = bgColor
render(
  <>
    <div id="borders">
      <div style={stylez} />
      <div style={stylez} />
      <div style={stylez} />
      <div style={stylez} />
    </div>
    <Suspense fallback={null}>
      <App />
    </Suspense>
  </>,
  document.getElementById('root')
)
function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });
  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return windowSize;
}